import { useEffect } from "react";
import { getBackgrounds, getIcons } from "../slices/tile";
import { getDashboardList } from "../slices/dashboard";
import { getTypes } from "../slices/data";
import { fetchConnections, fetchConnectionTypes } from "../slices/connection";
import { getFilters } from "../slices/filter";
import Connection from "../types/connection";
import ConnectionType from "../types/connectionType";
import { fetchEmailGroups } from "../slices/emailGroup";
import { getSchedules } from "../slices/schedule";
import { getIncidentTypes } from "../slices/settings";
import Option from "../types/option";
import { TileTypes } from "../types/tileType";
import FilterValue from "../types/filterValue";
import Filter from "../types/filter";

export const useTileOptions = ({
  formValues,
  resetField,
  dispatch,
  setConnectionList,
  setDataTypeList,
  setTileTypeList,
  connectionTypes,
  tileType,
  svAlertsEnabled,
  filters,
  setValue,
  watchOfflineEnabled,
  watchThresholdEnabled,
  watchReportGroups,
  dashboardOptions,
  dashboardId,
  settings,
  setEnabledConnectionTypes,
  dataTypeList,
  tileTypeList,
}: any) => {
  useEffect(() => {
    if (
      tileType === "dashboard" &&
      (!formValues.dataType || !formValues.type)
    ) {
      setValue(
        "dataType",
        dataTypeList?.find((x: Option) => x.id === 5)
      );
      setValue(
        "type",
        tileTypeList?.find((x: Option) => x.id === TileTypes.Dashboard)
      );
    }
  }, [
    dataTypeList,
    tileTypeList,
    tileType,
    setValue,
    formValues.dataType,
    formValues.type,
  ]);

  useEffect(() => {
    if (!!formValues.filters || !!formValues.filterSelections) {
      let existingFilters: FilterValue[] = formValues.filters;
      let unavailableFilters: FilterValue[] = existingFilters?.filter((x) =>
        filters?.every((f: Filter) => f.id !== x.filterId)
      );

      let existingSelections: Filter[] = formValues.filterSelections;
      let unavailableSelections = existingSelections?.filter((x) =>
        filters?.every((f: Filter) => f.id !== x.id)
      );

      if (!!unavailableFilters && !!unavailableFilters.length) {
        let selectedFilters: FilterValue[] = existingFilters?.filter((x) =>
          filters?.some((f: Filter) => f.id === x.filterId)
        );

        setValue("filters", selectedFilters);
      }

      if (!!unavailableSelections && !!unavailableSelections.length) {
        let selectedFilters = existingSelections?.filter((x) =>
          filters?.some((f: Filter) => f.id === x.id)
        );

        setValue("filterSelections", selectedFilters);
      }
    }
  }, [formValues.filters, formValues.filterSelections, filters, setValue]);

  useEffect(() => {
    if (watchOfflineEnabled) {
      setValue("alerts.offline.alertType", { id: 1, value: "" });
    }
  }, [watchOfflineEnabled, setValue, formValues.alerts]);

  useEffect(() => {
    if (watchThresholdEnabled) {
      setValue("alerts.threshold.alertType", { id: 2, value: "" });
    }
  }, [watchThresholdEnabled, setValue, formValues.alerts]);

  useEffect(() => {
    if (watchReportGroups?.length > 0) {
      setValue("scheduledReport.enabled", true);
    }
  }, [watchReportGroups, setValue]);

  useEffect(() => {
    if (!!dashboardOptions && !!dashboardId) {
      setValue(
        "dashboard",
        dashboardOptions.find((x: Option) => x.id === dashboardId)
      );
    }
  }, [setValue, dashboardId, dashboardOptions]);

  useEffect(() => {
    if (!!settings && !!settings.enabledConnectionTypes) {
      let allowedConnectionTypes: ConnectionType[] = connectionTypes?.filter(
        (x: ConnectionType) =>
          settings.enabledConnectionTypes?.some(
            (c: ConnectionType) => c.id === x.id
          )
      );
      setEnabledConnectionTypes(allowedConnectionTypes);
    }
  }, [settings, connectionTypes, setEnabledConnectionTypes]);

  useEffect(() => {
    dispatch(getDashboardList());
    if (tileType === "tile") dispatch(fetchConnectionTypes());
    if (tileType === "tile") dispatch(fetchEmailGroups());
    if (tileType === "tile") dispatch(getSchedules());
  }, [dispatch, tileType]);

  useEffect(() => {
    if (formValues.connectionType && !!connectionTypes) {
      const connectionType = connectionTypes.find(
        (x: ConnectionType) => x.id === formValues.connectionType.id
      );
      dispatch(fetchConnections()).then((result: any) => {
        const connections: Connection[] = result.payload.filter(
          (c: any) => c.connectionType?.id === connectionType?.id
        );

        if (connections && connections.length > 1) {
          let selectAll = {
            id: -1,
            name: "Select All",
          } as Connection;

          connections.unshift(selectAll);
        }

        setConnectionList(connections);
      });

      dispatch(getTypes()).then((result: any) => {
        const dataTypes = result.payload.filter(
          (type: any) =>
            type.id !== 5 &&
            connectionType.supportedDataTypes
              ?.map((x: any) => !!x && x.id)
              .includes(type.id)
        );
        setDataTypeList(dataTypes);
      });
    } else if (tileType === "dashboard") {
      dispatch(getTypes()).then((result: any) => {
        const dataTypes = result.payload.filter((type: any) => type.id === 5);
        setDataTypeList(dataTypes);
      });
    }
  }, [
    connectionTypes,
    formValues.connectionType,
    dispatch,
    setConnectionList,
    setDataTypeList,
    tileType,
  ]);

  useEffect(() => {
    if (formValues.dataType) {
      setTileTypeList(formValues.dataType.supportedTileTypes);
    }
  }, [formValues.dataType, setTileTypeList]);

  useEffect(() => {
    if (!!formValues.type) {
      if (!!formValues.type.hasBackground) dispatch(getBackgrounds());
      if (!!formValues.type.hasIcon) dispatch(getIcons());
    }
  }, [formValues.type, dispatch]);

  useEffect(() => {
    if (
      formValues.type?.hasFilters &&
      formValues.connections &&
      formValues.dataType
    ) {
      dispatch(getFilters(formValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.type,
    formValues.connections,
    formValues.dataType,
    formValues.reportType,
    dispatch,
  ]);

  useEffect(() => {
    if (tileType === "tile" && svAlertsEnabled) {
      dispatch(getIncidentTypes());
    }
  }, [dispatch, svAlertsEnabled, tileType]);

  useEffect(() => {
    if (!formValues.connectionType) {
      resetField("connections");
      resetField("dataType");
      resetField("type");
      resetField("filters");
      resetField("filterSelections");
    }
  }, [resetField, formValues.connectionType]);

  useEffect(() => {
    if (!formValues.dataType) {
      resetField("type");
      resetField("filters");
      resetField("filterSelections");
    }
  }, [resetField, formValues.dataType]);

  useEffect(() => {
    if (!formValues.type) {
      resetField("backgroundId");
      resetField("tileIconType");
      resetField("filters");
      resetField("filterSelections");
    }
  }, [resetField, formValues.type]);

  useEffect(() => {
    if (!formValues.connections) {
      resetField("filters");
      resetField("filterSelections");
    }
  }, [resetField, formValues.connections]);
};
